import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  Input,
  EventEmitter,
  Output
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Team } from 'src/app/shared/models/team/team';
import { AuthService } from 'src/app/auth/auth.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DateLessThanValidator } from 'src/app/shared/validators/date.validator';
import { Subscription, combineLatest } from 'rxjs';
import { Position } from 'src/app/shared/models/team/position';
import { TeamService } from 'src/app/shared/services/team.service';
import moment from 'moment';
import {
  MultiselectEvent,
  MultiselectItem
} from 'src/shared-app-portal/components/multiselect/multiselect.component';
import { ApplicationDefinitionSummary } from 'src/app/shared/models/application-definition/application-definition-summary';
import { ApplicationDefinitionService } from 'src/app/shared/services/application-definition.service';
import { OpportunityBoardService } from 'src/app/shared/services/opportunity-board.service';
import { PositionListing } from 'src/app/shared/models/positionListing';
import { PositionListingWrite } from 'src/app/shared/models/positionListingWrite';
import { SeatStatus } from 'src/app/shared/models/team/seatStatus';
import { NotEmptyValidator } from 'src/app/shared/validators/not-empty.validator';
import { AttributeEntityType } from 'src/app/shared/models/attributes/attributeEntityType';
import { AttributeService } from 'src/app/shared/services/attribute.service';
import { AttributeDefinition } from 'src/app/shared/models/attributes/attributeDefinition';
import { MatDialog } from '@angular/material/dialog';
import { PreviewApplicationModalComponent } from 'src/app/shared/components/preview-application-modal/preview-application-modal.component';
import { ResponsiveService } from 'src/app/shared/services/responsive.service';

@Component({
  selector: 'opportunity-listing-details',
  templateUrl: 'opportunity-listing-details.html',
  styleUrls: ['opportunity-listing-details.scss'],
  standalone: false
})
export class OpportunityListingDetailsComponent implements OnInit, OnDestroy {
  @ViewChild('name', { static: false }) nameInput: ElementRef;
  team: Team;
  teamId: string;
  position: Position;
  positionId: string;
  opportunityListingForm: FormGroup<{
    description: FormControl<string>;
    startDate: FormControl<Date>;
    endDate: FormControl<Date>;
    selectedForm: FormControl<ApplicationDefinitionSummary>;
    hasDeadline: FormControl<boolean>;
    deadline: FormControl<Date>;
    autoAdd: FormControl<boolean>;
  }>;
  selectedStartDate = 'Same as team start date';
  selectedEndDate = 'Same as team end date';
  listingHasDeadline: boolean = false;
  selectedForm: ApplicationDefinitionSummary;
  selectedStatus = 'Accepted';
  boards: MultiselectItem[] = [];
  selectedBoards: MultiselectItem[] = [];
  teamForms: ApplicationDefinitionSummary[];
  private subscriptions: Subscription[] = [];
  mode: string;
  listing: PositionListing;
  boardId: string;
  @Output() close = new EventEmitter();
  @Output() open = new EventEmitter();
  @Input() isSaving = false;
  @Input() notUsingRouter = false;
  attributes: AttributeDefinition[] = [];
  attributeSubscription: Subscription;

  constructor(
    public authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private teamService: TeamService,
    private applicationDefinitionService: ApplicationDefinitionService,
    private boardService: OpportunityBoardService,
    private formBuilder: FormBuilder,
    private attributeService: AttributeService,
    private responsiveService: ResponsiveService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    if (!this.notUsingRouter) {
      this.subscriptions.push(
        this.route.params.subscribe((params) => {
          const teamId =
            this.route.snapshot.parent.params['id'] ||
            this.route.snapshot.parent.parent.params['teamId'];
          const positionId = this.route.snapshot.params['positionId'];
          this.initialize(teamId, positionId);
        })
      );
    }
    this.attributeSubscription = this.attributeService.attributes.subscribe((attributes) => {
      if (!attributes) return;
      this.attributes = attributes.filter((x) => x.entityType == AttributeEntityType.Team);
    });
  }

  updateDates() {
    if (this.listing?.opportunitySeatStartDate) {
      this.selectedStartDate = 'Specific date';
      this.opportunityListingForm.controls.startDate.enable();
      this.opportunityListingForm.controls.startDate.addValidators([Validators.required]);
      this.opportunityListingForm.controls.startDate.markAsTouched();
    } else {
      this.selectedStartDate = 'Same as team start date';
      this.onSelectedStartDateChange();
    }
    if (this.listing?.opportunitySeatEndDate) {
      this.selectedEndDate = 'Specific date';
      this.opportunityListingForm.controls.endDate.enable();
      this.opportunityListingForm.controls.endDate.addValidators([Validators.required]);
      this.opportunityListingForm.controls.endDate.markAsTouched();
    } else {
      this.selectedEndDate = 'Same as team end date';
      this.onSelectedEndDateChange();
    }
  }

  async previewForm(): Promise<void> {
    const summary = this.opportunityListingForm.controls.selectedForm.value;
    if (!summary) {
      return;
    }
    const form = await this.applicationDefinitionService.getOne(summary.id);
    PreviewApplicationModalComponent.open(this.dialog, form);
  }

  ngOnDestroy(): void {
    this.resetStateAndUnSubscribe();
    this.attributeSubscription?.unsubscribe();
  }

  private resetStateAndUnSubscribe = () => {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this.subscriptions = [];
    this.listing = null;
  };

  hasDeadlineChanged() {
    if (this.opportunityListingForm.controls.hasDeadline.value) {
      this.opportunityListingForm.controls.deadline.setValidators([
        Validators.required,
        NotEmptyValidator()
      ]);
      this.opportunityListingForm.controls.deadline.updateValueAndValidity();
    } else {
      this.opportunityListingForm.controls.deadline.clearValidators();
      this.opportunityListingForm.controls.deadline.updateValueAndValidity();
      this.opportunityListingForm.controls.deadline.setValue(null);
    }
  }

  startDateChange() {
    const form = this.opportunityListingForm.value;
    let startDate = form.startDate;
    if (this.team.startDate && moment(startDate).isBefore(moment(this.team.startDate))) {
      this.opportunityListingForm.controls['startDate'].setErrors({
        startDateBeforeTeamStartDate: 'Start date cannot be before the team start date'
      });
    } else if (this.team.endDate && moment(startDate).isAfter(moment(this.team.endDate))) {
      this.opportunityListingForm.controls['startDate'].setErrors({
        startDateCannotBeAfterTeamEndDate: 'Start date cannot be after the team end date'
      });
    }
  }

  endDateChange() {
    const form = this.opportunityListingForm.value;
    let endDate = form.endDate;
    if (this.team.startDate && moment(endDate).isBefore(moment(this.team.startDate))) {
      this.opportunityListingForm.controls['endDate'].setErrors({
        endDateCannotBeBeforeTeamStartDate: 'End date cannot be before the team start date'
      });
    } else if (this.team.endDate && moment(endDate).isAfter(moment(this.team.endDate))) {
      this.opportunityListingForm.controls['endDate'].setErrors({
        endDateCannotBeAfterTeamEndDate: 'End date cannot be after the team end date'
      });
    }
  }

  getStartDatePlaceholder() {
    let placeHolder;
    if (this.selectedStartDate == 'Same as team start date') {
      placeHolder = this.team.startDate
        ? moment.utc(this.team.startDate).format('MM/DD/YYYY')
        : 'No start date';
    }
    return placeHolder;
  }

  getEndDatePlaceholder() {
    let placeHolder;
    if (this.selectedEndDate == 'Same as team end date') {
      placeHolder = this.team.endDate
        ? moment.utc(this.team.endDate).format('MM/DD/YYYY')
        : 'No end date';
    }
    return placeHolder;
  }

  public onSelectedStartDateChange(): void {
    if (this.selectedStartDate == 'Same as team start date') {
      this.opportunityListingForm.controls.startDate.removeValidators([Validators.required]);
      this.opportunityListingForm.controls.startDate.disable();
    } else {
      this.opportunityListingForm.controls.startDate.enable();
      this.opportunityListingForm.controls.startDate.addValidators([Validators.required]);
    }
    this.opportunityListingForm.controls.startDate.setValue(null);
    this.opportunityListingForm.controls.startDate.markAsTouched();
  }

  public onSelectedEndDateChange(): void {
    if (this.selectedEndDate == 'Same as team end date') {
      this.opportunityListingForm?.controls['endDate'].removeValidators([Validators.required]);
      this.opportunityListingForm?.controls['endDate'].disable();
    } else {
      this.opportunityListingForm?.controls['endDate'].enable();
      this.opportunityListingForm?.controls['endDate'].addValidators([Validators.required]);
    }
    this.opportunityListingForm?.controls['endDate'].setValue(null);
    this.opportunityListingForm?.controls.endDate.markAsTouched();
  }

  multiSelectChanged(event: MultiselectEvent) {
    this.selectedBoards = event.selectedItems.map((t) => new MultiselectItem(t.id, t.name));
  }

  closeListing() {
    if (this.notUsingRouter) {
      this.isSaving = false;
      this.opportunityListingForm = null;
      this.close.emit();
    } else {
      const betaMode = this.route.snapshot.parent.routeConfig.path == 'roster';
      if (betaMode) {
        this.router.navigate(['teams/beta/' + this.teamId + '/roster']);
        this.responsiveService.toggleDrawerOpenClose(false);
        return;
      }
      this.router.navigate(['teams/' + this.teamId + '/details']);
    }
  }

  saveListing() {
    if (this.opportunityListingForm?.valid) {
      const form = this.opportunityListingForm.value;
      let oppSeatStatus: any =
        this.selectedStatus == SeatStatus[SeatStatus.Accepted]
          ? SeatStatus[SeatStatus.Accepted]
          : SeatStatus[SeatStatus.Picked];
      let listing: PositionListingWrite = {
        teamId: this.teamId,
        positionId: this.positionId,
        formId: form.selectedForm.id,
        description: form.description,
        opportunitySeatStartDate: form.startDate,
        opportunitySeatEndDate: form.endDate,
        listingDeadline: form.deadline,
        opportunitySeatStatus: form.autoAdd ? oppSeatStatus : SeatStatus[SeatStatus.Bench],
        opportunityBoardIds: this.selectedBoards.map((t) => t.id)
      };
      if (this.listing?.id && this.mode == 'Edit') {
        this.teamService
          .updateListing(this.teamId, this.listing.id, listing, this.notUsingRouter)
          .subscribe((res) => {
            if (res) {
              this.closeListing();
            }
          });
      } else {
        this.teamService.createListing(this.teamId, listing).subscribe((res) => {
          if (res) {
            this.closeListing();
          }
        });
      }
    }
  }

  deleteListing() {
    if (this.listing?.id) {
      this.teamService
        .deleteListing(this.teamId, this.positionId, this.listing.id)
        .subscribe((res) => {
          if (res) {
            this.closeListing();
          }
        });
    }
  }

  edit(positionId: string, teamId: string) {
    if (this.notUsingRouter) {
      this.initialize(teamId, positionId);
      this.open.emit();
    }
  }

  private initialize(teamId: string, positionId: string) {
    this.resetStateAndUnSubscribe();
    this.teamId = teamId;
    this.positionId = positionId;
    this.boardId = this.route.snapshot.params['boardId'];
    if (this.boardId) {
      this.teamService.getTeamById(this.teamId);
    }
    this.boardService.getAll();
    this.applicationDefinitionService.getAll();
    let combined = combineLatest([
      this.teamService.team,
      this.applicationDefinitionService.allApplicationDefinitions,
      this.boardService.allBoards
    ]);

    this.subscriptions.push(
      combined.subscribe(([team, forms, boards]) => {
        if (!team || !forms || !boards) return;
        this.team = team;
        if (!this.team) return;
        this.position = this.team.positions.find((x) => x.id == this.positionId);
        if (!this.position) return;
        this.mode = this.position.positionListings?.length > 0 ? 'Edit' : 'Create';
        if (this.mode == 'Edit') {
          this.listing = this.position.positionListings[0];
        }

        this.teamForms = forms.filter(
          (x) => x.isArchived == false && x.hasExpired == false && !x.isPrivate
        );
        let activeBoards = boards.filter((x) => x.isActive);
        this.boards = activeBoards.map((t) => new MultiselectItem(t.id, t.title));

        if (this.listing && this.listing.id && this.mode == 'Edit') {
          if (this.listing.formId) {
            this.selectedForm = this.teamForms.find((x) => x.id == this.listing.formId);
          }

          if (this.listing.opportunityBoardIds) {
            let selectedBoards = activeBoards.filter((board) =>
              this.listing.opportunityBoardIds.includes(board.id)
            );
            this.selectedBoards = selectedBoards.map((t) => new MultiselectItem(t.id, t.title));
          }

          if (this.listing.listingDeadline) {
            this.listingHasDeadline = true;
          } else {
            this.listingHasDeadline = false;
          }
        }

        const listingStatus = this.listing?.opportunitySeatStatus.toString();

        const isAutoAdd = listingStatus != SeatStatus[SeatStatus.Bench];

        this.opportunityListingForm = this.formBuilder.group({
          description: this.mode == 'Edit' ? this.listing.description : null,
          startDate: this.mode == 'Edit' ? this.listing.opportunitySeatStartDate : null,
          endDate: this.mode == 'Edit' ? this.listing.opportunitySeatEndDate : null,
          selectedForm: [this.selectedForm, [Validators.required]],
          hasDeadline: this.listingHasDeadline,
          deadline: this.mode == 'Edit' ? this.listing.listingDeadline : null,
          autoAdd: isAutoAdd
        });

        if (isAutoAdd) {
          this.selectedStatus = listingStatus;
        }
        this.opportunityListingForm.addValidators([
          DateLessThanValidator('startDate', 'endDate', true)
        ]);

        this.updateDates();
        this.opportunityListingForm.valueChanges.subscribe(() => {
          if (!this.opportunityListingForm.controls.startDate.hasError('required')) {
            if (this.opportunityListingForm.errors?.startDateBeforeEndDate) {
              this.opportunityListingForm.controls.startDate.setErrors({
                startDateBeforeEndDate: true
              });
              this.opportunityListingForm.controls.startDate.markAsTouched();
            } else {
              this.opportunityListingForm.controls.startDate.setErrors(null);
              this.startDateChange();
            }
          }
        });
      })
    );
  }
}
